import React from "react";
import { Box, MenuItem, Select, Chip } from "@mui/material";
import type { SelectChangeEvent, SelectProps } from "@mui/material";
import { alpha, styled, useTheme } from "@mui/material/styles";
import { ArrowDown2 } from "iconsax-react";
import { useTranslation } from "react-i18next";
import { RADIUS } from "../../../theme/mixins";

interface StyledMultiSelectProps
  extends Omit<SelectProps, "onChange" | "value"> {
  label?: string;
  onChange?: (value: string[]) => void;
  value?: string[];
  options: { label: string; value: string }[];
  mode?: "light" | "dark";
  placeholder?: string;
}

const CustomSelect = styled(Select, {
  shouldForwardProp: (prop) => prop !== "mode",
})<{ mode?: "light" | "dark" }>(({ theme, mode }) => ({
  borderRadius: RADIUS.input,
  backgroundColor: theme.palette.common.white,
  color: theme.palette.text.primary,
  "& .MuiOutlinedInput-notchedOutline": {
    border:
      mode === "dark"
        ? "1px solid transparent"
        : `1px solid ${theme.palette.border.strong}`,
  },
  "&:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: theme.palette.border.grey,
  },
  // "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#C7CDD4" },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: theme.palette.primary.main,
    borderWidth: 1,
  },
  "& .MuiSelect-select": { color: theme.palette.text.primary },
  "& .MuiSelect-icon": { color: theme.palette.text.secondary },
}));

const SelectDropdownIcon: React.FC = () => {
  const theme = useTheme();
  return (
    <ArrowDown2
      size={18}
      color={theme.palette.text.secondary}
      style={{ marginRight: 12 }}
    />
  );
};

const StyledMultiSelect: React.FC<StyledMultiSelectProps> = ({
  label,
  options,
  onChange,
  value = [],
  mode = "dark",
  placeholder,
  ...props
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const [open, setOpen] = React.useState(false);
  const resolvedPlaceholder = placeholder ?? t('components.fields.selectPlaceholder');

  const handleChange = (event: SelectChangeEvent<unknown>) => {
    onChange?.(event.target.value as string[]);
    setOpen(false);
  };

  return (
    <Box sx={{ width: "100%" }}>
      {label !== undefined && label !== "" && (
        <Box
          component="label"
          sx={{
            display: "block",
            fontSize: "13px",
            fontWeight: 600,
            marginBottom: "6px",
            color:
              mode === "dark"
                ? alpha(theme.palette.common.white, 0.82)
                : theme.palette.text.primary,
          }}
        >
          {label}
        </Box>
      )}

      <CustomSelect
        {...props}
        multiple
        value={value}
        mode={mode}
        fullWidth
        displayEmpty
        onChange={handleChange}
        open={open}
        onOpen={() => setOpen(true)}
        onClose={() => setOpen(false)}
        renderValue={(selected) => {
          const arr = selected as string[];
          if (!arr || arr.length === 0) {
            return (
              <span style={{ color: theme.palette.text.disabled }}>
                {resolvedPlaceholder}
              </span>
            );
          }
          return (
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
              {arr.map((v) => {
                const opt = options.find((o) => o.value === v);
                return (
                  <Chip
                    key={v}
                    label={opt?.label || v}
                    size="small"
                    sx={{
                      backgroundColor: theme.palette.surface.cyan,
                      color: theme.palette.brand.icon,
                      border: `1px solid ${theme.palette.border.cyan}`,
                      fontSize: "12px",
                      height: "24px",
                    }}
                  />
                );
              })}
            </Box>
          );
        }}
        IconComponent={SelectDropdownIcon}
        MenuProps={{
          PaperProps: {
            sx: {
              mt: 0.5,
              borderRadius: "12px",
              boxShadow: theme.customShadows.dropdown,
              "& .MuiMenuItem-root": {
                color: "#014A4E",
                fontWeight: 400,
                fontSize: "14px",
                "&:hover": { backgroundColor: alpha(theme.palette.primary.main, 0.1) },
                "&.Mui-selected": {
                  backgroundColor: alpha(theme.palette.primary.main, 0.12),
                  fontWeight: 600,
                },
                "&.Mui-selected:hover": {
                  backgroundColor: alpha(theme.palette.primary.main, 0.16),
                },
              },
            },
          },
        }}
      >
        {options.map((opt) => (
          <MenuItem key={opt.value} value={opt.value}>
            {opt.label}
          </MenuItem>
        ))}
      </CustomSelect>
    </Box>
  );
};

export default StyledMultiSelect;
